import { useState } from 'react';
import { isAxiosError } from 'axios';
import { createClient, updateClientTest, type ClientDetail } from '@/api/clients';
import { showToast } from '@/lib/toast';
import { buildCreatePayload, type WizardState } from './wizardState';

/**
 * BFF отдаёт 502 (см. rethrowUpstreamError()/BadGatewayException в
 * clients.service.ts) именно тогда, когда сам не дождался ответа от старой
 * админки — не когда та ответила ошибкой. Это не значит "не создался": на
 * Windows SSO-регистрация внутри Account::createUser() может зависнуть на
 * TCP-коннекте заметно дольше своего таймаута, но PHP всё равно доводит
 * создание до конца (см. докблок createClient() в api/clients.ts) — обычная
 * фраза "не удалось создать" тут вводит в заблуждение, аккаунт зачастую уже есть.
 */
const UPSTREAM_UNREACHABLE_MESSAGE =
  'Не удалось дождаться ответа от старой админки — но клиент, скорее всего, всё же создался ' +
  '(создание идёт постепенно). Проверьте список клиентов через минуту, прежде чем создавать повторно.';

/**
 * Отправка мастера по клику "Создать" — единственная точка, где что-либо
 * реально пишется в базу (см. докблок ClientCreatePage.tsx): сначала
 * POST /clients (шаги 1-3 целиком, создаёт аккаунт+админа+кураторов+баланс за
 * один вызов), затем последовательно updateClientTest() на реальном новом id
 * для каждого теста/цены со шагов 4-5. Тесты — не Promise.all: при частичном
 * отказе последовательный проход проще размечает, какие именно тесты не
 * применились, а порядок вызовов не важен (независимые записи).
 */
export function useSubmitWizard() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (state: WizardState): Promise<ClientDetail | null> => {
    setSubmitting(true);
    setError(null);

    let created: ClientDetail;
    try {
      created = await createClient(buildCreatePayload(state));
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 502) {
        setError(UPSTREAM_UNREACHABLE_MESSAGE);
      } else {
        const message = isAxiosError<{ message?: string }>(err) ? err.response?.data?.message : null;
        setError(message || 'Не удалось создать клиента. Проверьте заполненные поля.');
      }
      setSubmitting(false);
      return null;
    }

    const testUpdates: { testId: number; payload: { attached?: boolean; cost?: number } }[] = [];
    for (const testId of state.selectedTests.keys()) {
      const cost = state.libraryPrices.get(testId);
      testUpdates.push({ testId, payload: cost === undefined ? { attached: true } : { attached: true, cost } });
    }
    for (const [testId, cost] of state.monoscalePrices.entries()) {
      testUpdates.push({ testId, payload: { cost } });
    }

    let failedCount = 0;
    for (const { testId, payload } of testUpdates) {
      try {
        await updateClientTest(created.id, testId, payload);
      } catch {
        failedCount += 1;
      }
    }

    if (failedCount > 0) {
      showToast(
        `Клиент создан, но не удалось применить ${failedCount} тест(ов)/цен — проверьте вкладки «Тесты»/«Цены» в карточке клиента.`,
      );
    } else {
      showToast('Клиент создан');
    }

    setSubmitting(false);
    return created;
  };

  return { submitting, error, submit };
}
